
import { Component, ViewEncapsulation, ViewChild, ChangeDetectorRef, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { SAUTableModule } from '@some-angular-utils/table';
import { SAUModalService } from '@some-angular-utils/modal';
import { Urls } from '../../../apiConnection/urls';
import { CustomDeleteComponent } from '../../../components/custom-delete/custom-delete.component';
import { InvoiceTemplateComponent } from '../invoice-template/document.component';
import { AddButtonComponent } from '@/components/add-button/add-button.component';
import { ReactiveFormsModule } from '@angular/forms';
import { ApiService } from '@/apiConnection/ApiService';
import { AuthenticationService } from '@/apiConnection/authentication.service';
import { DocumentReportCellsComponent } from '@/components/document-report-cells/document-report-cells.component';
import { Utils } from '@/utils/utils';

@Component({
  imports: [
    RouterModule,
    SAUTableModule,
    AddButtonComponent,
    ReactiveFormsModule,
    DocumentReportCellsComponent,
  ],
  templateUrl: './documents-list.page.html',
  styleUrl: './documents-list.page.scss',
  encapsulation: ViewEncapsulation.None
})


export class DocumentListPage {

  @ViewChild(InvoiceTemplateComponent)
  InvoiceTemplateComponent!: InvoiceTemplateComponent;

  @ViewChild('sauTable')
  sauTable: { refresh: () => void; getItems: () => void; } | undefined;

  private sauModalService = inject(SAUModalService);

  loading = false;
  printing = false;
  orientation: "dynamic" | "horizontal" | "vertical" = 'dynamic'
  showOptions = true
  limitNumber = 10

  public url = Urls.urls.documents;
  public docType: string | null = null;

  public headers = [
    { name: 'Número', key: 'type' },
    { name: 'Fecha', key: 'date', type: 'date' },
    { name: 'Cliente', key: 'client.name' },
    { name: 'Total', key: 'total' },
    { name: 'Usuario', key: 'user.name' },
    { name: 'Relación', key: 'relations' },
  ];

  public headersWithoutRelations = this.headers.filter(h => h.key !== 'relations' && h.key !== 'user.name');

  public filterConfigBackup: any = null
  public filterConfig: any = {
    orderParamName: 'sort',
    orderByFields: [
      { field: 'created_at', label: 'Fecha', defaultValue: true },
      { field: 'category_id', label: 'Familia', defaultValue: true },
    ],
    order: ['type', 'dateRange', 'client', 'category', 'product', 'provider'],
    mobile: ['type', 'dateRange'],
    form: {
      type: {
        name: 'Estado',
        key: 'types',
        type: 'selectSimple',
        dropdowns: [
          { id: 'all', name: 'Todos' },
          { id: 'factura', name: 'Facturas' },
          { id: 'albaran', name: 'Albaranes' },
          { id: 'devolucion', name: 'Devoluciones' },
          { id: 'presupuesto', name: 'Presupuesto' },
        ],
        defaultValue: 'all'
      },
      dateRange: {
        name: 'Periodo',
        key: 'date_start',
        keyTo: 'date_end',
        type: 'dateRange',
        defaultValue: []
      },
      client: {
        name: 'Cliente',
        key: 'client_id',
        type: 'selectSimple',
        dropdowns: [],
      },
      category: {
        name: 'Familia',
        key: 'category_id',
        type: 'selectMultiple',
        dropdowns: [],
      },
      product: {
        name: 'Producto',
        key: 'products',
        type: 'selectSimple',
        dropdowns: [],
      },
      provider: {
        name: 'Proveedor',
        key: 'provider_id',
        type: 'selectSimple',
        dropdowns: [],
      },
    }
  };


  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private apiService: ApiService,
    private authService: AuthenticationService,
  ) {
    this.docType = this.route.snapshot.data['docType'] ?? null;

    if (this.docType) {
      this.filterConfig.form.type.defaultValue = this.docType;
      this.filterConfig.order = this.filterConfig.order.filter((field: string) => field !== 'type');
      this.filterConfig.mobile = this.filterConfig.mobile.filter((field: string) => field !== 'type');
    } else {
      // "Informe de ventas" (sales-reports root, no single docType): always albaranes + ventas al
      // contado (facturas) + devoluciones, never presupuestos — no type selector to pick from.
      this.filterConfig.form.type.defaultValue = 'albaran,factura,devolucion';
      this.filterConfig.order = this.filterConfig.order.filter((field: string) => field !== 'type');
      this.filterConfig.mobile = this.filterConfig.mobile.filter((field: string) => field !== 'type');
    }

  }

  canShowDocType(docType: string): boolean {
    const allowed = this.authService.getAllowedDocumentTypes();
    return allowed === null || allowed.includes(docType);
  }

  showEditAndDeleteButtons(item: { type: string, document_ref: number, status: string }): boolean {
    if (item.type === 'albaran' && item.document_ref != null) return false;
    if (item.status === 'draft') return true;
    return (item.type == 'albaran' || item.type == 'presupuesto') && item.document_ref == null;
  }

  showPrintButton(item: { status: string }): boolean {
    return item.status === null;
  }

  canCloneItem(item: { type: string }): boolean {
    return item.type === 'presupuesto';
  }

  private duplicatingIds = new Set<number>();

  isDuplicating(item: { id: number }): boolean {
    return this.duplicatingIds.has(item.id);
  }

  duplicateDocument(item: { id: number }): void {
    if (this.isDuplicating(item)) return;
    this.duplicatingIds.add(item.id);

    this.apiService.save(`${Urls.urls.documents}/${item.id}/duplicate`, {}, 0).subscribe({
      next: (response: any) => {
        this.duplicatingIds.delete(item.id);
        Utils.notification.success('Presupuesto duplicado correctamente');
        window.open(`/budgets/edit/${response.document.id}`, '_blank');
        this.sauTable?.getItems();
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.duplicatingIds.delete(item.id);
        Utils.notification.alert(Utils.extractErrorMessage(error) || 'No se pudo duplicar el presupuesto');
        this.cdr.detectChanges();
      },
    });
  }

  print(item: { id: number }) {
    window.open(`/invoice/print/${item.id}`, '_blank');
  }

  showDetails(item: { id: number }) {
    const base = this.docType === 'presupuesto' ? 'budgets' : 'sales-reports';
    window.open(`/${base}/view/${item.id}`, '_blank');
  }

  toggleEditForm(item: { id: number }) {
    this.router.navigate(['edit', item.id], { relativeTo: this.route });
  }

  openDeleteForm(item: { id: number } = { id: 0 }) {
    const modalRef = this.sauModalService.open(CustomDeleteComponent, {});
    modalRef.componentInstance.id = item.id;
    modalRef.componentInstance.url = this.url;
    modalRef.componentInstance.item = 'documento';

    modalRef.componentInstance.acceptFunction.subscribe(() => {
      this.sauTable?.getItems();
    });

    modalRef.componentInstance.closeFunction.subscribe(() => {
      modalRef.dismiss();
    });
  }
  ngOnInit(): void {
    this.getDropdowns();
  }

  public dropdowns: any = {
    clients: [],
    products: [],
    categories: [],
    providers: [],
  };

  onSubmit(): void {

    this.cdr.detectChanges();
    this.sauTable?.refresh();

  }

  getDropdowns() {
    const config = { clients: {}, products: {}, categories: {}, providers: {} };
    this.apiService.getSelectors(Urls.urls.selectors, config).subscribe({
      next: (data: any) => {
        this.dropdowns.categories = data.categories || [];
        this.dropdowns.clients = data.clients || [];
        this.dropdowns.products = data.products || [];
        this.dropdowns.providers = data.providers || [];

        this.filterConfig.form.category.dropdowns.push(...data.categories || [])
        this.filterConfig.form.client.dropdowns.push(...data.clients || [])
        this.filterConfig.form.product.dropdowns.push(...data.products || [])
        this.filterConfig.form.provider.dropdowns.push(...data.providers || [])

        this.cdr.detectChanges();
      }
    });
  }

  printReport() {
    if (this.printing) return;
    this.printing = true;

    this.filterConfigBackup = this.filterConfig

    this.orientation = 'horizontal'
    this.showOptions = false
    this.filterConfig = null
    this.limitNumber = 9999999
    this.cdr.detectChanges();
    this.sauTable?.refresh();

  }

  finishLoad(event: any) {
    if (this.printing) {
      window.print(); // this will stop automatically

      this.orientation = 'dynamic'
      this.showOptions = true
      this.filterConfig = this.filterConfigBackup
      this.limitNumber = 10
      this.printing = false;
      this.cdr.detectChanges();
      this.sauTable?.refresh();
    }
  }

}